<template>
  <div class="flex gap-1 pt-1 max-w-full">
    <div class="flex grow gap-1 max-w-full">
      <div class="grow flex flex-col max-w-full">
        <LinkToSelectorItem
          :data="node"
          :level="level"
          :hasChildren="hasChildren"
          :isSelected="isSelected(node.id)"
          :isOpen="isOpen"
          :state="state"
          :searchTerm="searchTerm"
          :type="type"
          :showState="!node.id.includes('ungrouped-')"
          @toggleChildren="toggleChildren"
          :multiple="multiple"
          :selectable="
            buildingIsSelectable &&
            !node.id.includes('ungrouped-') &&
            !disabledItems.includes(node.id)
          "
          @updateState="
            emit('updateState', { node: node, state: state }, multiple)
          "
          :class="{
            hidden: !showBuildings && level === 1,
          }"
        />
        <div
          v-if="isOpen && node.children && node.children.length"
          class="flex flex-col relative max-w-full"
          :class="{ 'ltr:pl-2 rtl:pr-2': isChild }"
        >
          <div
            v-for="(child, index) in node.children"
            :key="child.id"
            class="relative"
          >
            <div
              v-if="isChild"
              class="absolute -top-0 ltr:left-0 rtl:right-0 w-4 border-primary ltr:border-l rtl:border-r"
              :class="index === node.children.length - 1 ? 'h-5' : 'h-full'"
            />
            <div class="flex items-center relative max-w-full">
              <div
                v-if="isChild"
                class="w-2 h-2 ltr:rounded-bl-xs rtl:rounded-br-xs ltr:border-l rtl:border-r border-b border-primary absolute top-[18px] ltr:left-0 rtl:right-0"
              />
              <div
                class="flex-grow max-w-full"
                :class="{ 'ltr:pl-2 rtl:pr-2': isChild }"
              >
                <TreeNode
                  :node="child"
                  :level="level + 1"
                  :selectedNodeIds="selectedNodeIds"
                  :shouldAutoOpen="shouldAutoOpen(child)"
                  :searchTerm="searchTerm"
                  :showBuildings="showBuildings"
                  :disabledItems="disabledItems"
                  :breadcrumb="getFullPath(child)"
                  :type="type"
                  :multiple="multiple"
                  @updateState="emit('updateState', $event)"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="node.id.includes('ungrouped-')"
          class="w-[54px] h-[1px] mx-auto mb-1 mt-2 border-b border-primary"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { LinkToSelectorItem } from "@/SharedComponents/LinkToSelector";
import { States } from "@/Composables/useSpacesAndAssetsTree";

// Define props
const props = defineProps({
  node: { type: Object, required: true },
  type: { type: String, required: true },
  breadcrumb: { type: String, required: true },
  searchTerm: { type: String, default: "" },
  selectedNodeIds: { type: Array, required: true },
  shouldAutoOpen: { type: Boolean, default: false },
  disabledItems: { type: Array, default: () => [] },
  level: { type: Number, required: true },
  multiple: { type: Boolean, default: true },
  showBuildings: { type: Boolean, default: true },
  buildingIsSelectable: { type: Boolean, default: true },
});

/**
 * Emits an event to update filters
 * @type {function}
 */
const emit = defineEmits(["updateState"]);

/**
 * Reactive reference to control the open/closed state of the node
 * @type {import('vue').Ref<boolean>}
 */
const isOpen = ref(false);

/**
 * Computed property to check if the node has children
 * @type {import('vue').ComputedRef<boolean>}
 */
const hasChildren = computed(
  () => props.node.children && props.node.children.length > 0
);

/**
 * Determines whether a child should be auto-opened
 * @param {Object} child - The child node object
 * @returns {boolean} True if the child should be auto-opened, false otherwise
 */
const shouldAutoOpen = (node) => {
  if (props.multiple) return false;

  const hasSelectedChild = (node) => {
    return (
      node.children?.some(
        (child) => isSelected(child.id) || hasSelectedChild(child)
      ) ?? false
    );
  };

  return !isSelected(node.id) && hasSelectedChild(node);
};

/**
 * Generates the full path for a child node
 * @param {Object} child - The child node object
 * @returns {string} The full path including the current breadcrumb and child name
 */
const getFullPath = (child) => {
  return props.breadcrumb + " > " + child.name;
};

/**
 * Watches for changes in the shouldAutoOpen prop
 * Opens the node if shouldAutoOpen becomes true
 */
watch(
  () => props.shouldAutoOpen,
  (newVal) => {
    if (newVal) {
      isOpen.value = true;
    }
  },
  { immediate: true }
);

/**
 * Checks if a node is selected based on its ID
 * @param {string} id - The ID of the node to check
 * @returns {boolean} True if the node is selected, false otherwise
 */
const isSelected = (id) => {
  if (!props.selectedNodeIds) return false;

  return props.selectedNodeIds.includes(id);
};

/**
 * Toggles the open/closed state of the node's children
 */
const toggleChildren = () => {
  isOpen.value = !isOpen.value;
};

/**
 * Recursively checks if all children of a node are selected
 * @param {Object} node - The node to check
 * @returns {boolean} True if all children are selected, false otherwise
 */
const checkChildrenSelected = (node) => {
  if (!node.children || node.children.length === 0) return true;

  return node.children.every((child) => {
    const areChildrenSelected = checkChildrenSelected(child);
    return isSelected(child.id) && areChildrenSelected;
  });
};

/**
 * Computed property that checks if all children of the current node are selected
 * @type {ComputedRef<boolean>}
 */
const allChildrenSelected = computed(() => {
  return checkChildrenSelected(props.node);
});

/**
 * Computed property that checks if all direct children of the current node are selected
 * @type {ComputedRef<boolean>}
 */
const allDirectChildrenSelected = computed(() => {
  if (!props.node.children || props.node.children.length === 0) return true;

  return props.node.children.every((child) => {
    return isSelected(child.id);
  });
});

/**
 * Computed property that checks if some direct children of the current node are selected
 * @type {ComputedRef<boolean>}
 */
const someDirectChildrenSelected = computed(() => {
  if (!props.node.children || props.node.children.length === 0) return true;

  return props.node.children.some((child) => {
    return isSelected(child.id);
  });
});

/**
 * Computed property that determines the state of the current node
 * @type {ComputedRef<string>}
 */
const state = computed(() => {
  if (!props.multiple) return isSelected.value ? States.SINGLE : States.EMPTY;

  const checkChildrenSelected = (node) => {
    if (!node.children || node.children.length === 0) return false;
    return node.children.some((child) => {
      return isSelected(child.id) || checkChildrenSelected(child);
    });
  };

  if (!isSelected(props.node.id) && checkChildrenSelected(props.node)) {
    return States.INTERMEDIATE;
  }

  if (isSelected(props.node.id) && !allChildrenSelected.value) {
    return States.SINGLE;
  }

  if (isSelected(props.node.id) && allChildrenSelected.value) {
    return States.ALL;
  }

  if (
    !isSelected(props.node.id) &&
    !allDirectChildrenSelected.value &&
    someDirectChildrenSelected.value
  ) {
    return States.INTERMEDIATE;
  }

  if (
    !isSelected(props.node.id) &&
    !allChildrenSelected.value &&
    allDirectChildrenSelected.value &&
    someDirectChildrenSelected.value
  ) {
    return States.INTERMEDIATE;
  }

  return States.EMPTY;
});

const isChild = props.showBuildings || props.level > 1;
</script>
